import React, { forwardRef, useRef, useImperativeHandle, useEffect } from "react";
import { Dropdown, RegularButton, TextArea } from "colbi_web_ui/lib/components/inputs";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import styles from "./AuditNotesForm.module.sass";
import { datetime } from "colbi_web_ui/lib/utils";

let origin;
let showAuditNoteHistory;
let entity;

const snakeCaseToPascalCase = (str) => {
    const camelize = str.replace(/_\w/g, (m) => m[1].toUpperCase());
    return camelize.substring(0, 1).toUpperCase() + camelize.substring(1);
};

const AuditNotesForm = (props, ref) => {
    const { data, auditNoteStatusTypes, status, onChange, onSave } = props;

    const formRef = useRef();
    const { auditNotesHistory, findingId, AuditNote, AuditStatus } = data || {};

    const { auditStatusTypes } = auditNoteStatusTypes || {};

    const { i18n, params, user } = useGlobalValue();

    const { name } = user || {};

    const { projectId, importId, fiscalYear, modulePrefix, entities: allEntities } = params;

    const entities = (allEntities || "").split("/");
    const entityPairs = (entities || []).reduce((pairs, current) => {
        if (!pairs[pairs.length - 1] || pairs[pairs.length - 1].length === 2) {
            pairs.push([]);
        }
        if (pairs[pairs.length - 1]) {
            pairs[pairs.length - 1].push(current);
        }
        return pairs;
    }, []);

    const entityIndex = entities.length % 2 === 0 ? entityPairs.length - 1 : entityPairs.length - 2;

    const entityName = (entityPairs[entityIndex] || [])[0];
    const entityId = (entityPairs[entityIndex] || [])[1];

    entity = entityName;

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                        },
                    },
                },
            });
    };

    const refs = (auditNotesHistory || []).reduce((acc, value) => {
        acc[value.id] = React.createRef();
        return acc;
    }, {});

    const scrollToBottomList = (id) =>
        refs[id].current.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });

    useEffect(() => {
        if (auditNotesHistory && auditNotesHistory.length && refs && showAuditNoteHistory) {
            scrollToBottomList(auditNotesHistory[auditNotesHistory.length - 1]["id"]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auditNotesHistory]);

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));

    return status !== "loading" ? (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (formRef.current.checkValidity()) {
                    data["projectId"] = projectId;
                    data["sourceId"] = findingId;
                    data["importId"] = importId;
                    data["fiscalYear"] = fiscalYear;
                    data["Origin"] = origin;
                    data["modulePrefix"] = modulePrefix;
                    data["EntityName"] = snakeCaseToPascalCase(entityName || "");
                    data["RowID"] = entityId;
                    onSave && onSave();
                } else {
                    formRef.current.reportValidity();
                }
            }}
        >
            <div className={styles["group-content"]}>
                {showAuditNoteHistory && auditNotesHistory && auditNotesHistory.length ? (
                    <div className={styles["audit-note-container-border"]}>
                        {auditNotesHistory.map((auditNotes) => {
                            const { auditNote, username, id, auditStatus, createdAt } = auditNotes;
                            return (
                                <div key={`${username}_${id}`} ref={refs[id]}>
                                    <div
                                        className={
                                            isAuditNoteUserWriter(name, username)
                                                ? `${styles["audit-note-info"]} ${styles["own-info"]}`
                                                : styles["audit-note-info"]
                                        }
                                    >
                                        <h5 className={styles["audit-note-user"]}>
                                            {isAuditNoteUserWriter(name, username)
                                                ? "Me"
                                                : username}
                                        </h5>
                                        <h5
                                            className={`${styles["audit-note-date-time"]} ${
                                                isAuditNoteUserWriter(name, username)
                                                    ? styles["own-date-time-container-adjust"]
                                                    : styles["date-time-container-adjust"]
                                            }`}
                                        >
                                            {datetime(new Date(createdAt))}
                                        </h5>
                                    </div>
                                    <div
                                        className={
                                            isAuditNoteUserWriter(name, username)
                                                ? styles["own-container"]
                                                : styles["container"]
                                        }
                                    >
                                        <label
                                            className={`${styles["audit-note-text"]} ${
                                                styles["status"]
                                            } ${
                                                isAuditNoteUserWriter(name, username)
                                                    ? styles["own-audit-message"]
                                                    : ""
                                            }`}
                                        >{i18n`${auditStatus ? auditStatus : ""}`}</label>
                                        <label
                                            className={`${styles["audit-note-text"]} ${
                                                isAuditNoteUserWriter(name, username)
                                                    ? styles["own-audit-message"]
                                                    : ""
                                            }`}
                                        >
                                            {auditNote}
                                        </label>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : null}
                <h5 className={styles["label"]}>{i18n`Audit Status`}</h5>
                <Dropdown
                    className={styles["dropdown"]}
                    placeholder={i18n`Audit Status`}
                    value={
                        AuditStatus && auditStatusTypes
                            ? auditStatusTypes.find(({ id }) => id === AuditStatus) ||
                              auditStatusTypes[0]
                            : null
                    }
                    options={auditStatusTypes}
                    required
                    onChange={(e) => {
                        handleChange(e, "AuditStatus", e.target.value.id);
                    }}
                />
                <div className={styles["audit-note-with-button"]}>
                    <TextArea
                        className={styles["input"]}
                        value={AuditNote || ""}
                        placeholder={i18n`Enter a Audit Note`}
                        onChange={(e) => {
                            handleChange(e, "AuditNote", e.target.value);
                        }}
                    />
                    <RegularButton primary className={`${styles["simple-button"]}`}>
                        {i18n`Submit`}
                    </RegularButton>
                </div>
            </div>
        </form>
    ) : null;
};

const AuditNotesFormRef = forwardRef(AuditNotesForm);

AuditNotesFormRef.queries = (args) => {
    origin = args.origin;
    showAuditNoteHistory = args.showAuditNoteHistory;
    return [
        {
            resource: `nc_audit_note_finding_history`,
            args: {
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
                importId: args.importId,
                findingId: args.findingId,
                origin: args.origin,
                entityId: args.entityId,
                entity: entity,
            },
            body: `auditNotesHistory{auditNote, userID, id, auditStatus, createdAt, username}, findingId`,
        },
        {
            resource: `nc_audit_note_status_types`,
            body: `auditStatusTypes{ id, name }`,
        },
    ];
};

AuditNotesFormRef.dataMapper = ["data", "auditNoteStatusTypes"];

export default AuditNotesFormRef;

function isAuditNoteUserWriter(username, auditNoteUsername) {
    return auditNoteUsername === username;
}
